/** @jsx jsx */
// Building blocks after the university's design (www2.uni-luebeck.de/studium/):
// picture tiles in a two-column grid, an outlined button, and a link list in a
// bordered box with a picture next to it. Styled with emotion and the --iti-*
// variables, so they work in dark mode and inside the shadow DOM of the custom
// elements on the home page.
import { jsx } from '@emotion/react';
import styled from '@emotion/styled';
import React from 'react';

export function Arrow({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false"
      fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12h17M13 5l7 7-7 7" />
    </svg>
  );
}

export const TileGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '30px',
  margin: '24px 0 30px',
  '@media (max-width: 700px)': {
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: '16px',
  },
});

const TileLink = styled('a')({
  position: 'relative',
  display: 'block',
  aspectRatio: '23 / 12',
  overflow: 'hidden',
  color: '#fff',
  background: 'var(--iti-card)',
  textDecoration: 'none',
  '&:hover, &:focus-visible': { color: '#fff', textDecoration: 'none' },
  '& img': {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform .4s ease',
  },
  '& img.logo': {
    objectFit: 'contain',
    inset: '6% 12% auto',
    width: '76%',
    height: '42%',
  },
  '&:hover img, &:focus-visible img': { transform: 'scale(1.04)' },
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(to top, rgba(0, 32, 38, .78) 0%, rgba(0, 32, 38, .35) 38%, rgba(0, 32, 38, 0) 62%)',
  },
  // logo only: the text sits in a band in the site colour that grows with it
  '&.logo-only::after': { background: 'none' },
  '&.logo-only .text': {
    left: 0,
    right: 0,
    bottom: 0,
    padding: '12px 64px 14px 22px',
    background: 'var(--iti-band, #336f7b)',
  },
  // no picture at all: the site colour with a fine pattern
  '&.plain': {
    background: 'var(--iti-band, #336f7b) repeating-linear-gradient(135deg, rgba(255,255,255,.05) 0 2px, transparent 2px 14px)',
  },
  '&.plain::after': { background: 'none' },
  '&:focus-visible': { outline: '3px solid var(--iti-accent)', outlineOffset: '3px' },
});

const TileText = styled('div')({
  position: 'absolute',
  left: '22px',
  right: '64px',
  bottom: '18px',
  zIndex: 1,
  '& .title': {
    display: 'block',
    fontSize: '24px',
    lineHeight: 1.25,
    fontWeight: 600,
    textShadow: '0 1px 3px rgba(0, 0, 0, .35)',
  },
  '& .teaser': {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    marginTop: '4px',
    fontSize: '16px',
    lineHeight: 1.35,
    opacity: .92,
  },
  '@media (max-width: 700px)': {
    left: '16px',
    bottom: '14px',
    '& .title': { fontSize: '20px' },
    '& .teaser': { fontSize: '15px', WebkitLineClamp: 1 },
  },
});

const TileArrow = styled('span')({
  position: 'absolute',
  right: '20px',
  bottom: '18px',
  zIndex: 1,
  lineHeight: 0,
});

type PictureTileProps = { title: string, teaser?: string, image?: string, logo?: string, href: string };

// A picture with the title and a short teaser on it; a project with only a logo
// shows the logo on a light field above a band in the site colour.
export function PictureTile({ title, teaser, image, logo, href }: PictureTileProps) {
  const logoOnly = !image && !!logo;
  return (
    <TileLink href={href} className={logoOnly ? 'logo-only' : (image ? '' : 'plain')}>
      {image && <img src={image} alt="" loading="lazy" />}
      {logoOnly && <img className="logo" src={logo} alt="" loading="lazy" />}
      <TileText className="text">
        <span className="title">{title}</span>
        {teaser && <span className="teaser">{teaser}</span>}
      </TileText>
      <TileArrow><Arrow size={30} /></TileArrow>
    </TileLink>
  );
}

export const OutlineButton = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '24px',
  minWidth: 'min(400px, 100%)',
  padding: '18px 20px',
  border: '1px solid var(--iti-border)',
  color: 'var(--iti-text)',
  fontWeight: 700,
  textDecoration: 'none',
  '& svg': { color: 'var(--iti-heading)', flexShrink: 0 },
  '&:hover, &:focus-visible': { borderColor: 'var(--iti-heading)', color: 'var(--iti-text)', textDecoration: 'none' },
});

const ListSection = styled('section')({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
  gap: '60px',
  alignItems: 'start',
  margin: '0 0 64px',
  '&.no-picture': { gridTemplateColumns: 'minmax(0, 1fr)' },
  '& > .picture': { position: 'relative', display: 'block', color: '#fff', aspectRatio: '3 / 2', overflow: 'hidden' },
  '& > .picture img': { width: '100%', height: '100%', objectFit: 'cover' },
  '& > .picture svg': { position: 'absolute', right: '20px', bottom: '18px' },
  '& h2': { margin: '0 0 8px' },
  '& .intro': { margin: '0 0 28px' },
  '@media (max-width: 900px)': {
    gridTemplateColumns: 'minmax(0, 1fr)',
    gap: '24px',
    '& > .picture': { order: -1 },
  },
});

const LinkBox = styled('ul')({
  listStyle: 'none',
  margin: 0,
  background: 'var(--iti-bg)',
  padding: '20px 32px',
  border: '1px solid var(--iti-border)',
  borderRadius: '6px',
  maxWidth: '530px',
  '& li a': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '20px',
    padding: '18px 0',
    color: 'var(--iti-text)',
    fontSize: '20px',
    lineHeight: 1.35,
    fontWeight: 700,
    textDecoration: 'none',
  },
  '& li a small': { display: 'block', fontSize: '15px', fontWeight: 400, color: 'var(--iti-muted)' },
  '& li a svg': { color: 'var(--iti-heading)', flexShrink: 0 },
  '& li a:hover, & li a:focus-visible': { color: 'var(--iti-heading)', textDecoration: 'none' },
  '@media (max-width: 700px)': { padding: '8px 20px', '& li a': { fontSize: '18px', padding: '14px 0' } },
});

type LinkItem = { label: string, href: string, note?: string };
type LinkListProps = { title?: string, intro?: React.ReactNode, items: LinkItem[], image?: string, imageHref?: string };

// A heading, a short text and a box of links, with a picture next to it (below on phones).
export function LinkListSection({ title, intro, items, image, imageHref }: LinkListProps) {
  return (
    <ListSection className={image ? '' : 'no-picture'}>
      <div>
        {title && <h2>{title}</h2>}
        {intro && <div className="intro">{intro}</div>}
        <LinkBox>
          {items.map((item) => (
            <li key={item.href + item.label}>
              <a href={item.href}>
                <span>{item.label}{item.note && <small>{item.note}</small>}</span>
                <Arrow />
              </a>
            </li>
          ))}
        </LinkBox>
      </div>
      {image && (imageHref
        ? <a className="picture" href={imageHref}><img src={image} alt="" loading="lazy" /><Arrow size={30} /></a>
        : <div className="picture"><img src={image} alt="" loading="lazy" /></div>)}
    </ListSection>
  );
}

// The first paragraph of a description as plain text, for a teaser.
export function teaserOf(html?: string, length = 180): string {
  if (!html) {
    return '';
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const first = [...doc.querySelectorAll('p')].map((p) => p.textContent?.trim() ?? '').find((t) => t.length > 0) ?? '';
  const text = first || (doc.body.textContent ?? '').trim();
  return text.length > length ? `${text.slice(0, length).replace(/\s+\S*$/, '')} …` : text;
}

// A course's name without the module codes the title repeats, and the codes as a note.
const CODES = /\s*[-–]?\s*\(?\b(?:CS|RO|ME|MA|IT)\s?\d{3,4}[^,)]*(?:[,/+]\s*(?:CS|RO|ME|MA|IT)?\s?\d{3,4}[^,)]*)*\)?/;
export function courseLabel(course: any): { label: string, note?: string } {
  const title: string = course.title ?? course.name ?? '';
  const codes = title.match(CODES)?.[0]?.replace(/^[\s\-–(]+|[)\s]+$/g, '');
  const label = title.replace(CODES, '').replace(/\s*[-–]\s*$/, '').trim() || title;
  const cid = typeof course.cid === 'string' && /\b[A-Z]{2}\s?\d{3,4}/.test(course.cid) ? course.cid : undefined;
  return { label, note: cid ?? codes };
}

// Moodle's course images need a login; only our own pictures can be shown.
export function coursePicture(course: any): string | undefined {
  return course?.picture ?? (course?.image && !/pluginfile\.php/.test(course.image) ? course.image : undefined);
}

// The pictures of a project or group: a photo for the tile, else its logo.
export function tilePictures(obj: any): { image?: string, logo?: string } {
  const images = obj?.images ?? [];
  const photo = images.find((i: any) => i.type === 'background') ?? images.find((i: any) => i.type === 'other' && /\.(jpe?g|webp)$/i.test(i.url ?? ''));
  const logo = images.find((i: any) => i.type === 'logo') ?? images.find((i: any) => i.type === 'other');
  return { image: photo?.url ?? obj?.image ?? undefined, logo: logo?.url };
}
